import { useEffect, useMemo } from "react"
import { Box, InputAdornment, InputBase, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace"
import clsx from "clsx"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"

import { SearchIcon, CloseIcon as CrossIcon } from "components/icons"
import { SearchBasePopup } from "components/domains/search/SearchBasePopup"
import { SearchResult } from "components/domains/search/SearchResult"
import { AdSuggestList } from "components/domains/ads"
import { CloseIconButton } from "components/Button"
import { SearchListItem } from "./SearchListItem"

export const SearchPopupContent = ({
  openSearchState,
  onClose,
  popupType,
  onChange,
  onSelectResult,
  onClickSuggestedItem,
  searchResultItems,
  searchInputRef,
  searchValue,
  inputProps,
  closeButtonProps,
  closeIconProps,
  searchBasePopupProps = {},
  closeIcon: CloseIcon = KeyboardBackspaceIcon,
  searchResultProps,
  onClickClearSearchValue,
  suggestedListItems = [],
  venueFilterComponent = null,
}) => {
  const { sx: closeButtonSxProps } = closeButtonProps || {}
  const { sx: closeIconSxProps } = closeIconProps || {}
  const { sx: inputSxProps } = inputProps || {}
  const { t } = useTranslation({ defaultValue: null })
  const theme = useTheme()
  const searchBarStyles =
    theme.components[`VuiSearchPopupContent`]?.styleOverrides || {}
  const searchIcon = useMemo(
    () => getIcon(theme?.assets?.searchIcon) || <SearchIcon />,
    [theme]
  )
  const { hasNextPage, endScrollRef, ...restSearchResultProps } =
    searchResultProps || {}

  const closeIcon = useMemo(() => {
    const iconSx = deepmerge(searchBarStyles.closeButtonIcon, closeIconSxProps)
    return (
      getIcon(theme?.assets?.backIcon, {
        ...closeIconProps,
        sx: iconSx,
      }) || <CloseIcon {...closeIconProps} sx={iconSx} />
    )
  }, [
    theme,
    closeIconProps,
    closeIconSxProps,
    searchBarStyles.closeButtonIcon,
    CloseIcon,
  ])

  const isRtl = theme.isRtl

  useEffect(() => {
    const searchInputElem = searchInputRef.current
    if (searchInputElem) {
      searchInputElem.value = searchValue
      searchInputElem.focus()
    }
    // eslint-disable-next-line
  }, [searchInputRef.current])

  return (
    <SearchBasePopup
      {...searchBasePopupProps}
      open={openSearchState}
      onClose={onClose}
      type={popupType}
    >
      {/* SearchBar */}
      <Box
        sx={searchBarStyles.root}
        className={`popupType-${popupType} VuiSearchPopupContent-searchBar`}
      >
        <CloseIconButton
          {...closeButtonProps}
          icon={closeIcon}
          className={clsx(
            "VuiSearchPopupContent-closeButton",
            isRtl ? "is-rtl" : ""
          )}
          sx={deepmerge(searchBarStyles.closeButton, closeButtonSxProps)}
          onClick={onClose}
        />
        <InputBase
          className={`popupType-${popupType} VuiSearchPopupContent-searchBarInput`}
          fullWidth
          placeholder={t("Search")}
          onChange={onChange}
          inputRef={searchInputRef}
          startAdornment={
            <InputAdornment position="start">{searchIcon}</InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <CrossIcon onClick={onClickClearSearchValue} />
            </InputAdornment>
          }
          {...inputProps}
          sx={deepmerge(searchBarStyles.searchInput, inputSxProps)}
        />
        <Box
          className="VuiSearchPopupContent-venueFilterContainer"
          sx={searchBarStyles.venueFilterContainer}
        >
          {venueFilterComponent}
        </Box>
      </Box>

      {/* SearchResult + Suggested Brand*/}
      <SearchResult
        {...searchResultProps}
        startAdornment={
          suggestedListItems.length > 0 &&
          !searchValue && (
            <AdSuggestList
              sx={searchBarStyles.suggestedList}
              suggestedListItems={suggestedListItems}
              onClickSuggestedItem={onClickSuggestedItem}
            />
          )
        }
        popupType={popupType}
        items={searchResultItems}
        onSelect={onSelectResult}
        hasNextPage={hasNextPage}
        endScrollRef={endScrollRef}
        renderSearchItem={SearchListItem}
        {...restSearchResultProps}
      />
    </SearchBasePopup>
  )
}
